/**
 * The frame every signed-in page renders inside.
 *
 * The skip link is the part worth defending. Without it, a keyboard user
 * arriving on any page tabs through the entire navigation before reaching the
 * content — on every page, every time. It is the single highest-value
 * accessibility affordance in an application with a persistent sidebar, and it
 * costs six lines.
 *
 * It is visually hidden until focused, which is why it is not "just an
 * invisible link": a sighted keyboard user must be able to see where they are.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import "./layout.css";

export interface AppShellProps {
  /** Branding and account menu. */
  header: ReactNode;
  /** Primary navigation. Omit for pages with none. */
  sidebar?: ReactNode;
  children: ReactNode;
  /** Translated by the caller — this package holds no strings. */
  skipToContentLabel: string;
  navigationLabel: string;
  /**
   * The label of the bar that opens the navigation on a phone. When given,
   * the rail folds behind it below the tablet breakpoint; when omitted, the
   * rail is laid out in full above the page, as before.
   */
  menuLabel?: string;
}

export const MAIN_CONTENT_ID = "main-content";

export function AppShell({
  header,
  sidebar,
  children,
  skipToContentLabel,
  navigationLabel,
  menuLabel,
}: AppShellProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navId = useId();
  const collapsible = Boolean(menuLabel);

  const navRef = useRef<HTMLElement>(null);

  // Following a link closes the menu: on a phone the next page is what the
  // reader asked for, and it would otherwise open underneath the list. A
  // delegated native listener rather than `onClick` on the <nav>: the links
  // are the interactive elements, and a keyboard activation of one fires
  // `click` too, so nothing here needs a key handler of its own.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || !collapsible) return undefined;
    const closeOnLink = (event: Event) => {
      if ((event.target as Element | null)?.closest("a")) setMenuOpen(false);
    };
    nav.addEventListener("click", closeOnLink);
    return () => {
      nav.removeEventListener("click", closeOnLink);
    };
  }, [collapsible]);

  return (
    <div className="hardauto-shell">
      {/* First focusable element on the page, by document order. */}
      <a className="hardauto-skip-link" href={`#${MAIN_CONTENT_ID}`}>
        {skipToContentLabel}
      </a>

      <header className="hardauto-shell__header">{header}</header>

      <div className="hardauto-shell__body">
        {sidebar && collapsible ? (
          // Hidden by CSS above a phone's width, so a desktop never sees it.
          <button
            type="button"
            className="hardauto-shell__menu-toggle"
            aria-expanded={menuOpen}
            aria-controls={navId}
            onClick={() => {
              setMenuOpen((open) => !open);
            }}
          >
            <svg className="hardauto-shell__menu-icon" viewBox="0 0 24 24" aria-hidden="true">
              {menuOpen ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
            {menuLabel}
          </button>
        ) : null}
        {sidebar ? (
          // A named landmark: with more than one <nav> on a page, screen-reader
          // users get a list of them, and "navigation, navigation" is useless.
          <nav
            id={navId}
            ref={navRef}
            className="hardauto-shell__sidebar"
            aria-label={navigationLabel}
            data-collapsible={collapsible ? "true" : undefined}
            data-open={menuOpen ? "true" : undefined}
          >
            {sidebar}
          </nav>
        ) : null}

        {/* `tabIndex={-1}` makes the skip link's target focusable, without
            which the browser scrolls but leaves focus behind. */}
        <main id={MAIN_CONTENT_ID} className="hardauto-shell__main" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}

export interface PageProps {
  title: string;
  description?: string;
  /**
   * A short tracked label above the heading: what this record is, or where the
   * data came from.
   *
   * Not a subtitle. It carries the identifier and the provenance — "REF-1234 ·
   * verrouillé par Sage" — which belong above the name rather than beside it,
   * because the name is what the reader is looking for and the identifier is
   * what they check afterwards.
   */
  eyebrow?: ReactNode;
  /** A link back to the list this record came from. */
  back?: ReactNode;
  /** Buttons for the page as a whole. */
  actions?: ReactNode;
  children: ReactNode;
}

/** A page's heading, description and content. */
export function Page({ title, description, eyebrow, back, actions, children }: PageProps) {
  return (
    <div className="hardauto-page">
      {back ? <div className="hardauto-page__back">{back}</div> : null}
      <div className="hardauto-page__header">
        <div className="hardauto-page__headings">
          {eyebrow ? <p className="hardauto-eyebrow">{eyebrow}</p> : null}
          {/* Exactly one <h1> per page. Headings are how screen-reader users
              navigate; a page with none, or several, is a page with no map. */}
          <h1 className="hardauto-page__title">{title}</h1>
          {description ? <p className="hardauto-page__description">{description}</p> : null}
        </div>
        {actions ? <div className="hardauto-page__actions">{actions}</div> : null}
      </div>
      {children}
    </div>
  );
}

export interface CardProps {
  title?: string;
  description?: string;
  /**
   * Render the title as a small tracked label rather than a heading.
   *
   * Still an `<h2>`: the visual weight changes, the document outline does
   * not. A screen-reader user navigating by heading needs the card titles
   * whatever size they are drawn at.
   */
  compactTitle?: boolean;
  /** Buttons acting on this card's content. */
  actions?: ReactNode;
  footer?: ReactNode;
  /** Remove the body's padding, for a card that is entirely a table. */
  flush?: boolean;
  children: ReactNode;
}

/** A bounded region of related content. */
export function Card({
  title,
  description,
  compactTitle = false,
  actions,
  footer,
  flush = false,
  children,
}: CardProps) {
  return (
    <section className="hardauto-card" aria-label={title}>
      {title || actions ? (
        <header className="hardauto-card__header">
          <div>
            {title ? (
              <h2
                className={
                  compactTitle ? "hardauto-card__title hardauto-eyebrow" : "hardauto-card__title"
                }
              >
                {title}
              </h2>
            ) : null}
            {description ? <p className="hardauto-card__description">{description}</p> : null}
          </div>
          {actions ? <div className="hardauto-card__actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className="hardauto-card__body" data-flush={flush ? "true" : undefined}>
        {children}
      </div>
      {footer ? <footer className="hardauto-card__footer">{footer}</footer> : null}
    </section>
  );
}
